import React, { FC, useMemo, useCallback } from 'react';
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ComposedChart,
  Bar,
} from 'recharts';
import styled from 'styled-components';
import { TruthTableEntry } from '../../../common/model/dto/lot-calibration-data';
import Colours from '../../../design-system/colours';
import { useTranslation } from 'react-i18next';
import OvCalibrationChartTooltip from './OvCalibrationChartTooltip';
import { calculateDomainWithPadding } from '../../../utils/chart-domain-calculation';

interface OvCalibrationCandlestickChartProps {
  data: TruthTableEntry[];
  thresholds?: number[];
  unit: string;
}

interface CandlestickDataPoint {
  signal: number;
  open: number;
  close: number;
  high: number;
  low: number;
}

/**
 * Props for the CandlestickBar custom shape component
 */
interface CandlestickBarProps {
  x: number;
  y: number;
  width: number;
  payload: CandlestickDataPoint;
  domainMin: number;
  domainMax: number;
}


// Chart configuration constants
const CHART_HEIGHT = 400;
const CHART_TOP_MARGIN = 20;
const CHART_BOTTOM_MARGIN = 30;
const CHART_AREA_HEIGHT = CHART_HEIGHT - CHART_TOP_MARGIN - CHART_BOTTOM_MARGIN;
const CHART_BOTTOM_Y = CHART_HEIGHT - CHART_BOTTOM_MARGIN;
const PADDING_PERCENT = 0.25; // 25% padding for domain calculation
const BODY_WIDTH_RATIO = 0.5; // Body width as ratio of bar width
const BODY_HEIGHT = 4; // Fixed body height in pixels
const WICK_STROKE_WIDTH = 2;
const BODY_STROKE_WIDTH = 1;

/**
 * Candlestick chart component for visualizing calibration error margins
 * Displays base concentration as a body and error margins as wicks extending above and below
 * 
 * @param data - Truth table entries with concentration and error margin data
 * @param thresholds - Optional threshold values to display as reference lines
 * @param unit - Unit string for displaying concentration values
 */
const OvCalibrationCandlestickChart: FC<OvCalibrationCandlestickChartProps> = ({
  data,
  thresholds,
  unit,
}) => {
  const { t } = useTranslation();

  const chartData = useMemo<CandlestickDataPoint[]>(() => {
    return data
      .filter(
        (entry) =>
          entry.concentrationMinusError !== undefined &&
          entry.concentrationPlusError !== undefined
      )
      .map((entry) => ({
        signal: entry.signal,
        open: entry.concentration,
        close: entry.concentration,
        high: entry.concentrationPlusError!,
        low: entry.concentrationMinusError!,
      }));
  }, [data]);

  // Calculate domain with generous padding to prevent values from "hanging off"
  // Make sure we capture ALL values including high and low
  const { domainMin, domainMax } = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { domainMin: 0, domainMax: 100 };
    }
    const allValues = chartData.flatMap((d) => [d.high, d.low, d.close]);
    return calculateDomainWithPadding(allValues, PADDING_PERCENT);
  }, [chartData]);

  /**
   * Formats numbers for Y-axis display with appropriate rounding and notation
   * @param value - The numeric value to format
   * @returns Formatted string representation
   */
  const formatYAxisValue = useCallback((value: number): string => {
    if (value === 0) return '0';
    if (Math.abs(value) < 0.01) return value.toExponential(1);
    if (Math.abs(value) < 1) return value.toFixed(2);
    if (Math.abs(value) < 100) return value.toFixed(1);
    if (Math.abs(value) < 1000) return Math.round(value).toString();
    if (Math.abs(value) < 1000000) {
      const rounded = Math.round(value / 1000) * 1000;
      if (rounded >= 1000) {
        return `${(rounded / 1000).toFixed(rounded % 1000 === 0 ? 0 : 1)}k`;
      }
      return rounded.toString();
    }
    // For very large numbers, use exponential notation
    return value.toExponential(1);
  }, []);

  /**
   * Formats a concentration value for display
   */
  const formatConcentration = useCallback((value: number): string => {
    if (value < 1) return value.toFixed(2);
    if (value < 100) return value.toFixed(1);
    return Math.round(value).toString();
  }, []);

  /**
   * Extract data from candlestick chart payload for tooltip
   */
  const extractCandlestickData = useCallback(
    (payload: unknown) => {
      const data = payload as CandlestickDataPoint;
      return {
        signal: data.signal,
        base: data.close,
        high: data.high,
        low: data.low,
        unit,
        formatConcentration,
      };
    },
    [unit, formatConcentration]
  );

  /**
   * Custom shape component for rendering candlestick bars
   * Displays the base concentration as a small body and error margins as wicks
   */
  const CandlestickBar = useCallback((props: CandlestickBarProps) => {
    const { x, y, width, payload, domainMin, domainMax } = props;
    const { high, low, close } = payload;

    // Recharts provides 'y' which is the Y coordinate of the 'close' value
    // We need to calculate high and low positions relative to this
    const range = domainMax - domainMin;
    
    // Calculate the scale factor: how many pixels per unit value
    const pixelsPerValueUnit = CHART_AREA_HEIGHT / range;
    
    // Calculate offsets from close position
    // In SVG, y increases downward, so:
    // - higher concentration value = smaller y coordinate (higher on screen)
    // - lower concentration value = larger y coordinate (lower on screen)
    const highOffset = (high - close) * pixelsPerValueUnit;
    const lowOffset = (close - low) * pixelsPerValueUnit;
    
    // Calculate Y coordinates
    // 'y' from recharts is already the correct position for 'close'
    const closeY = y;
    const highY = closeY - highOffset; // high is above close (smaller y in SVG)
    const lowY = closeY + lowOffset; // low is below close (larger y in SVG)
    
    // Ensure lowY doesn't go below the chart bottom
    const clampedLowY = Math.min(lowY, CHART_BOTTOM_Y);

    // For candlestick chart: 
    // - Body represents close (and open, which equals close in our case)
    // - Since open = close, the body should be a small rectangle at the close position
    // - The wicks extend from the body to high/low
    const centerX = x + width / 2;
    const bodyWidth = width * BODY_WIDTH_RATIO;
    
    // Body position: centered vertically on closeY
    const bodyY = closeY - BODY_HEIGHT / 2;

    // Use default color
    const strokeColor = Colours.OV_BASE;
    const fillColor = Colours.OV_BASE;

    return (
      <g>
        {/* Upper wick - from body center to high */}
        <line
          x1={centerX}
          y1={closeY}
          x2={centerX}
          y2={highY}
          stroke={strokeColor}
          strokeWidth={WICK_STROKE_WIDTH}
          style={{ pointerEvents: 'none' }}
        />
        {/* Lower wick - from body center to low (clamped) */}
        <line
          x1={centerX}
          y1={closeY}
          x2={centerX}
          y2={clampedLowY}
          stroke={strokeColor}
          strokeWidth={WICK_STROKE_WIDTH}
          style={{ pointerEvents: 'none' }}
        />
        {/* Body - small rectangle at close position (open = close) */}
        <rect
          x={centerX - bodyWidth / 2}
          y={bodyY}
          width={bodyWidth}
          height={BODY_HEIGHT}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={BODY_STROKE_WIDTH}
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  }, []);

  // Early return after all hooks
  if (!chartData || chartData.length === 0) {
    return null;
  }

  return (
    <Container>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart
          data={chartData}
          margin={{ top: CHART_TOP_MARGIN, right: 30, left: 20, bottom: CHART_BOTTOM_MARGIN }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={Colours.GRAY['200']} />
          <XAxis
            dataKey="signal"
            label={{
              value: t('lots.calibration.chart.signal'),
              position: 'insideBottom',
              offset: -5,
              fill: Colours.OV_BASE,
            }}
            tick={{ fill: Colours.OV_BASE }}
          />
          <YAxis
            domain={[domainMin, domainMax]}
            tickFormatter={formatYAxisValue}
            label={{
              value: unit && unit !== 'null' && unit !== 'undefined' && unit.trim() !== ''
                ? `${t('lots.calibration.chart.concentration')} (${unit})`
                : t('lots.calibration.chart.concentration'),
              angle: -90,
              position: 'insideLeft',
              fill: Colours.OV_BASE,
            }}
            tick={{ fill: Colours.OV_BASE }}
            width={80}
          />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            content={(props: any) => (
              <OvCalibrationChartTooltip
                {...props}
                unit={unit}
                formatConcentration={formatConcentration}
                t={t}
                extractData={extractCandlestickData}
              />
            )}
          />
          {/* Threshold Lines */}
          {thresholds?.map((threshold, index) => (
            <ReferenceLine
              key={`threshold-${index}`}
              y={threshold}
              stroke={Colours.OV_RED}
              strokeDasharray="5 5"
              label={{
                value: `${threshold} ${unit}`,
                position: 'right',
                fill: Colours.OV_RED,
              }}
            />
          ))}
          {/* Candlestick visualization using custom shape */}
          <Bar
            dataKey="close"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            shape={(props: any) => {
              const payload = props.payload as CandlestickDataPoint;
              const candlestickProps: CandlestickBarProps = {
                x: props.x,
                y: props.y,
                width: props.width,
                payload,
                domainMin,
                domainMax,
              };
              return <CandlestickBar {...candlestickProps} />;
            }}
            fill={Colours.OV_BASE}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Container>
  );
};

export default OvCalibrationCandlestickChart;

const Container = styled.div`
  margin-top: 1rem;
  width: 100%;
`;

